import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { streamChatFileEvents } from "@/api";
import { reportError } from "@/telemetry/telemetry";
import { FILE_CHECKS_KEY } from "./useFileChecks";

/** Query keys that hold one chat file's content or state (its preview, its status read). */
function refetchFile(queryClient: QueryClient, fileId: string) {
  void queryClient.invalidateQueries({ queryKey: ["chat", "attachment", fileId] });
  void queryClient.invalidateQueries({
    queryKey: [FILE_CHECKS_KEY],
    predicate: (query) => (query.queryKey[1] as readonly string[] | undefined)?.includes(fileId) ?? false,
  });
}

/**
 * Follows a chat's live file-state notices (MHS-646) while it's open: a file whose checks finish, or whose state
 * changes, is read again at once (its status and, when it is open, its preview). Notices are best effort; the
 * status polling and the preview's own retry stay the fallback.
 */
export function useChatFileEvents(sessionUuid: string | undefined, enabled: boolean) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled || !sessionUuid) return;
    const controller = new AbortController();
    (async () => {
      for await (const event of streamChatFileEvents(sessionUuid, { signal: controller.signal })) {
        if (event.event === "attachment_state") refetchFile(queryClient, event.data.fileId);
        else if (event.event === "ready") void queryClient.invalidateQueries({ queryKey: [FILE_CHECKS_KEY] });
      }
    })().catch((error: unknown) => reportError(error, { source: "chatFileEvents" }));
    return () => controller.abort();
  }, [enabled, queryClient, sessionUuid]);
}
